"use client";

import * as React from "react";
import { cn } from "../lib/cn";
import { Label } from "./Label";
import { Input, type InputProps } from "./Input";
import { AuthErrorAlert } from "./AuthErrorAlert";

/**
 * AuthFieldRow - a labeled field with an optional leading icon and the brand
 * focus ring, for the auth card body.
 *
 * The unified screens repeated the same shape for every field: an uppercase
 * micro-label, a relatively-positioned wrapper with a leading icon absolutely
 * placed, and an `<input>` re-declaring the brand focus ring
 * (`focus:shadow-[0_0_0_3px_rgba(10,37,64,0.15)]`) by hand. This composes the
 * lib's `Label` + `Input` so the field inherits the canonical focus-visible
 * ring (`--ring`, the brand navy) and token colors instead of re-deriving them.
 *
 * Named `AuthFieldRow` (not `FieldRow`) on purpose: a private `FormFieldRow`
 * already lives inside `blocks/Form.tsx`, so the `Auth*` prefix keeps the auth
 * surface distinct and collision-free.
 *
 * For password fields, reach for `PasswordInput` directly (it ships the reveal
 * toggle + success check). This row is for plain text / email inputs; if you
 * need an icon + a password field, pass `<PasswordInput>` via `children`.
 *
 *   <AuthFieldRow label="Email" htmlFor="email" icon={<Mail className="h-[15px] w-[15px]" />}>
 *     <Input id="email" type="email" autoComplete="username" />
 *   </AuthFieldRow>
 *
 *   // Convenience: omit children to render the built-in <Input> with inputProps.
 *   <AuthFieldRow label="Email" htmlFor="email" error={emailError}
 *     inputProps={{ id: "email", type: "email", value, onChange }} />
 */
export interface AuthFieldRowProps {
  /** The field caption. */
  label: React.ReactNode;
  /** Links the label to the control. Pass the same value as the input's `id`. */
  htmlFor?: string;
  /** Optional leading glyph, placed at the input's left edge. */
  icon?: React.ReactNode;
  /** An error message; renders an `AuthErrorAlert` beneath the field when set. */
  error?: React.ReactNode;
  /** Render a custom control (e.g. `PasswordInput`). When omitted, a built-in `Input` is rendered. */
  children?: React.ReactNode;
  /** Props forwarded to the built-in `Input` when `children` is omitted. */
  inputProps?: InputProps;
  /** Extra classes on the row wrapper. */
  className?: string;
}

export const AuthFieldRow = React.forwardRef<HTMLDivElement, AuthFieldRowProps>(
  ({ label, htmlFor, icon, error, children, inputProps, className }, ref) => {
    const control =
      children ??
      (
        <Input
          id={htmlFor}
          {...inputProps}
          className={cn(icon && "pl-10", inputProps?.className)}
        />
      );

    return (
      <div ref={ref} className={cn("flex flex-col gap-1.5", className)}>
        <Label
          htmlFor={htmlFor}
          size="sm"
          className="text-[9.5px] font-medium uppercase tracking-[0.13em] text-muted-foreground"
        >
          {label}
        </Label>
        {icon ? (
          <div className="relative">
            <span
              aria-hidden
              className="pointer-events-none absolute left-[13px] top-1/2 flex -translate-y-1/2 text-muted-foreground"
            >
              {icon}
            </span>
            {control}
          </div>
        ) : (
          control
        )}
        {error ? (
          <AuthErrorAlert className="mt-1" animate={false}>
            {error}
          </AuthErrorAlert>
        ) : null}
      </div>
    );
  },
);
AuthFieldRow.displayName = "AuthFieldRow";
